import {
  AlertTriangle,
  Check,
  Clock3,
  FileCheck2,
  Pause,
  X,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import {
  useEffect,
  useId,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import type { AttendanceStatus } from "../types";
import { statusLabel } from "../lib/utils";

export function SchoolMark({
  compact = false,
  inverse = false,
}: {
  compact?: boolean;
  inverse?: boolean;
}) {
  return (
    <div className={`school-mark ${compact ? "school-mark--compact" : ""}`}>
      <img src="/school-mark.svg" alt="" className="school-mark__crest" />
      {!compact && (
        <div className="school-mark__copy">
          <strong className={inverse ? "text-inverse" : ""}>JosephCare</strong>
          <span className={inverse ? "text-inverse-muted" : ""}>SK St Joseph (M) Papar</span>
        </div>
      )}
    </div>
  );
}

export function Avatar({
  initials,
  size = "md",
  tone = 0,
  label,
}: {
  initials: string;
  size?: "sm" | "md" | "lg" | "xl";
  tone?: number;
  label?: string;
}) {
  return (
    <span
      className={`avatar avatar--${size} avatar--tone-${tone % 5}`}
      aria-label={label}
      role={label ? "img" : undefined}
    >
      {initials}
    </span>
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "tertiary" | "danger" | "gold";
  size?: "default" | "compact";
  icon?: LucideIcon;
  iconAfter?: LucideIcon;
  loading?: boolean;
  full?: boolean;
}

export function Button({
  variant = "primary",
  size = "default",
  icon: Icon,
  iconAfter: IconAfter,
  loading = false,
  full = false,
  className = "",
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={`button button--${variant} button--${size} ${full ? "button--full" : ""} ${className}`}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <span className="spinner" aria-hidden="true" /> : Icon ? <Icon size={18} /> : null}
      <span>{children}</span>
      {IconAfter ? <IconAfter size={18} /> : null}
    </button>
  );
}

export function IconButton({
  label,
  icon: Icon,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; icon: LucideIcon }) {
  return (
    <button className={`icon-button ${className}`} aria-label={label} title={label} {...props}>
      <Icon size={20} />
    </button>
  );
}

const statusIcons: Record<AttendanceStatus, LucideIcon> = {
  present: Check,
  late: Clock3,
  absent: X,
  excused: FileCheck2,
};

export function StatusBadge({
  status,
  compact = false,
}: {
  status: AttendanceStatus;
  compact?: boolean;
}) {
  const Icon = statusIcons[status];
  return (
    <span className={`status-badge status-badge--${status} ${compact ? "status-badge--compact" : ""}`}>
      <Icon size={13} strokeWidth={2.4} />
      <span>{statusLabel[status]}</span>
    </span>
  );
}

export function StateBadge({
  state,
}: {
  state: "active" | "inactive" | "offline" | "demo";
}) {
  const content = {
    active: { Icon: Check, label: "Aktif" },
    inactive: { Icon: Pause, label: "Tidak aktif" },
    offline: { Icon: AlertTriangle, label: "Luar talian" },
    demo: { Icon: FileCheck2, label: "Data demo" },
  }[state];
  return (
    <span className={`state-badge state-badge--${state}`}>
      <content.Icon size={13} />
      {content.label}
    </span>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}

export function MetricCard({
  label,
  value,
  context,
  icon: Icon,
  tone = "primary",
  trend,
}: {
  label: string;
  value: string | number;
  context: string;
  icon: LucideIcon;
  tone?: "primary" | "success" | "warning" | "danger" | "info";
  trend?: string;
}) {
  return (
    <section className="metric-card">
      <div className={`metric-card__icon metric-card__icon--${tone}`}>
        <Icon size={20} />
      </div>
      <div className="metric-card__body">
        <span className="metric-card__label">{label}</span>
        <strong className="metric-card__value">{value}</strong>
        <span className="metric-card__context">{context}</span>
      </div>
      {trend && <span className="metric-card__trend">{trend}</span>}
    </section>
  );
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = "md",
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  size?: "sm" | "md" | "lg" | "drawer";
  footer?: ReactNode;
}) {
  const titleId = useId();
  const descriptionId = useId();
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.classList.add("modal-open");
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("modal-open");
    };
  }, [onClose, open]);

  if (!open) return null;
  return (
    <div className="modal-layer" role="presentation">
      <button className="modal-scrim" aria-label="Tutup dialog" onClick={onClose} />
      <section
        className={`modal modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
      >
        <header className="modal__header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description && <p id={descriptionId}>{description}</p>}
          </div>
          <IconButton label="Tutup" icon={X} onClick={onClose} />
        </header>
        <div className="modal__body">{children}</div>
        {footer && <footer className="modal__footer">{footer}</footer>}
      </section>
    </div>
  );
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  danger = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Batal
          </Button>
          <Button
            variant={danger ? "danger" : "primary"}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className={`confirm-message ${danger ? "confirm-message--danger" : ""}`}>
        {danger ? <AlertTriangle size={22} /> : <FileCheck2 size={22} />}
        <p>{message}</p>
      </div>
    </Modal>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  demo = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
  demo?: boolean;
}) {
  return (
    <label className={`toggle-row ${disabled ? "toggle-row--disabled" : ""}`}>
      <span className="toggle-row__copy">
        <span className="toggle-row__label">
          {label}
          {demo && <span className="demo-tag">Demo</span>}
        </span>
        {description && <span className="toggle-row__description">{description}</span>}
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        disabled={disabled}
      />
      <span className="toggle" aria-hidden="true">
        <span />
      </span>
    </label>
  );
}

export function EmptyState({
  icon: Icon = FileCheck2,
  title,
  message,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="empty-state__icon">
        <Icon size={24} />
      </span>
      <h3>{title}</h3>
      <p>{message}</p>
      {action}
    </div>
  );
}

export function InlineNotice({
  tone = "info",
  title,
  children,
}: {
  tone?: "info" | "success" | "warning" | "danger" | "neutral";
  title?: string;
  children: ReactNode;
}) {
  const Icon =
    tone === "success"
      ? Check
      : tone === "warning"
        ? AlertTriangle
        : tone === "danger"
          ? XCircle
          : FileCheck2;
  return (
    <div className={`inline-notice inline-notice--${tone}`}>
      <Icon size={18} />
      <div>
        {title && <strong>{title}</strong>}
        <div>{children}</div>
      </div>
    </div>
  );
}

